<script lang="ts">
  import '$lib/styles/blog.css';
  import SherlockBenchmark from '$lib/components/SherlockBenchmark.svelte';
  import { trackCtaClick } from '$lib/utils/analytics';
  import type { PageData } from './$types';
  
  export let data: PageData;
  $: post = data.post;

  function handleArticleClick(event: MouseEvent) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a[href^="https://app.testmachine.ai"]');
    if (!(anchor instanceof HTMLAnchorElement)) return;

    const destinationUrl = anchor.getAttribute('href') || anchor.href;
    const ctaLocation = destinationUrl.includes('/share/') || destinationUrl.includes('/token/')
      ? 'blog_inline_scan_results'
      : 'blog_closing_cta';
    const image = anchor.querySelector('img');
    const ctaLabel = anchor.textContent?.trim() || image?.getAttribute('alt') || '';

    trackCtaClick(ctaLocation, ctaLabel, destinationUrl, post.slug);
  }

  function trackArticleCtaClicks(node: HTMLElement) {
    node.addEventListener('click', handleArticleClick);

    return {
      destroy() {
        node.removeEventListener('click', handleArticleClick);
      }
    };
  }
</script>

<svelte:head>
  <title>{post.title} — TestMachine</title>
  <meta name="description" content={post.seoDescription || post.excerpt} />
  
  <!-- Open Graph meta tags -->
  {#if post.slug === 'azimuth-evmbench-benchmark-results'}
    <meta property="og:title" content="State of the Art on EVMBench" />
    <meta property="og:description" content="Across 117 findings in 40 audits, Azimuth detected 101 — 86.3% recall, ahead of GPT-5.6 Codex at 61.54% on EVMBench." />
    <meta property="og:image" content="https://testmachine.ai/images/azimuth-evmbench-cover.jpg" />
  {:else}
    <meta property="og:title" content="{post.title} — TestMachine" />
    <meta property="og:description" content={post.seoDescription || post.excerpt} />
    <meta property="og:image" content="https://testmachine.ai/testmachine-og-logo.png" />
  {/if}
  <meta property="og:url" content="https://testmachine.ai/blog/{post.slug}" />
  <meta property="og:type" content="article" />
  <meta property="og:site_name" content="TestMachine" />
  
  <!-- Twitter Card meta tags -->
  <meta name="twitter:card" content="summary_large_image" />
  {#if post.slug === 'azimuth-evmbench-benchmark-results'}
    <meta name="twitter:title" content="State of the Art on EVMBench" />
    <meta name="twitter:description" content="Across 117 findings in 40 audits, Azimuth detected 101 — 86.3% recall, ahead of GPT-5.6 Codex at 61.54% on EVMBench." />
    <meta name="twitter:image" content="https://testmachine.ai/images/azimuth-evmbench-cover.jpg" />
  {:else}
    <meta name="twitter:title" content="{post.title} — TestMachine" />
    <meta name="twitter:description" content={post.seoDescription || post.excerpt} />
    <meta name="twitter:image" content="https://testmachine.ai/testmachine-og-logo.png" />
  {/if}
</svelte:head>

<section class="article-header">
  <div class="container">
    <div class="article-header-inner">
      <a href="/blog" class="article-back">&larr; Back to blog</a>
      <div class="article-meta">
        <span class="blog-card-tag">{post.tag}</span>
        <span class="blog-card-date">{post.date}</span>
      </div>
      <h1 class="article-title">{post.title}</h1>
    </div>
  </div>
</section>

<article class="article-body container" use:trackArticleCtaClicks>
  {#if post.slug === 'coinbase-dex-expansion'}
    <p><em>A behind-the-scenes look at how Coinbase uses TestMachine to safely scale access to millions of ERC-20 tokens on its new retail DEX integration.</em></p>

    <p>When Coinbase announced that users could now trade millions of ERC-20 DEX tokens directly from their Coinbase app, it marked a major moment for decentralized access — one that didn't compromise on safety.</p>

    <p>To understand where Coinbase was leveraging our platform, we sat down with Eric Meng, who leads security for Coinbase's new Retail DEX trading integration. Eric shared how TestMachine quietly powers this new layer of token safety, helping Coinbase safely scale to millions of tokens.</p>

    <h3>Scaling Trust in an Open Ecosystem</h3>

    <p>Eric began by describing the challenge behind the DEX integration.</p>

    <blockquote><p>"The entire DEX ecosystem on Base would now be available to retail users," he said. "But with that scale comes significant risk."</p></blockquote>

    <p>Those risks aren't theoretical. Many ERC-20s include hidden mechanics — confiscation clauses, proxy upgrades, or blacklist features — that can put users at risk long after a token goes live.</p>

    <blockquote><p>"Traditional audits can't always detect these behaviors fast enough," Eric explained. "That's where TestMachine steps in."</p></blockquote>

    <p>He elaborated that Coinbase's focus isn't just on finding code vulnerabilities, it's on uncovering malicious privileges hidden in plain sight.</p>

    <blockquote><p>"Finding contract vulnerabilities is a secondary objective," Eric said. "There are many security providers that focus on surfacing bugs, but for trading ERC-20 tokens, that's not how most users lose money. They're getting scammed through obfuscated functions that standard audits overlook. Our goal is to detect those hidden privileges early, and try to get to them first before our users do."</p></blockquote>

    <h3>A Deterministic Layer for Token Safety</h3>

    <p>At the heart of the integration is Predator, TestMachine's AI-driven engine that continuously simulates live contract behavior to detect risks before users ever see them.</p>

    <p>For Coinbase, this system has become essential infrastructure and a foundation for scaling safely. That continuous monitoring ensures that token behavior is validated on an ongoing basis, giving Coinbase the confidence to expand decentralized access without compromising its security posture.</p>

    <p>Eric pointed out that the same standards apply across all Coinbase token offerings, not just the DEX.</p>

    <blockquote><p>"TestMachine's product is seamlessly integrated into the overall risk process, including the centralized exchange and the decentralized exchange integration," he said.</p></blockquote>

    <p>That unified model allows Coinbase to apply consistent safety logic across every platform, from institutional custody to on-chain retail products.</p>

    <h3>Beyond Static Code Analysis</h3>

    <p>Static analysis provides a snapshot. A moment-in-time view of a token's code. But onchain assets evolve. Contracts can be upgraded, proxies re-pointed, or permissions silently expanded. These changes often happen after initial audits, creating blind spots that traditional scanners can't catch.</p>

    <p>TestMachine bridges that gap with continuous AI-driven behavior monitoring. Rather than analyzing code once, its system re-simulates tokens as they evolve, using machine learning models to detect behavioral shifts and surface new capabilities in near real time. This live process ensures that Coinbase's listings reflect the current behavior of every contract. Not just the version that was originally deployed.</p>

    <blockquote><p>"Tokens aren't static. They change, upgrade, and sometimes behave differently days or weeks after launch," Eric said. "Having an AI system that continuously re-evaluates those behaviors gives us a live picture of risk. That's what makes this integration so valuable."</p></blockquote>

    <p>That continuous visibility has become a core differentiator as Coinbase scales to millions of assets.</p>

    <h3>A New Standard for DEX Safety</h3>

    <p>Coinbase's DEX integration shows that safety and access can coexist. By pairing open markets with AI-driven behavioral intelligence, Coinbase and TestMachine are setting a new standard for how Web3 platforms grow responsibly.</p>

    <p>Behind every listed token is a layer of adaptive intelligence made up of automated simulations, deterministic checks, and continuous AI validation that ensure state changes are also captured, even as contracts evolve. Instead of treating audits as a single event, TestMachine transforms token security into a living system that updates as the blockchain itself changes.</p>

    <p>Under the hood, TestMachine's reinforcement learning engine observes how contracts behave inside a high-fidelity EVM simulation. The AI agent tries different combinations of function calls, explores new contract states, and learns which interactions might break a token's core guarantees. As it receives feedback from these attempts, the system becomes better at identifying the kinds of hidden privileges, state changes, and behavioral shifts that often appear only after deployment.</p>

    <p>These simulations run continuously inside TestMachine's controlled execution environment, which mirrors real blockchain conditions and provides a consistent sandbox for learning. Because this environment feeds the AI new behaviors as they emerge in the wild, it allows Coinbase to benefit from a system that improves over time. The result is a safety layer that adapts as quickly as new tokens and attack patterns appear.</p>

    <blockquote><p>"It is helpful to have an AI system that keeps learning from new token behaviors. It gives us an edge in this game of cat and mouse where risks will likely appear," Eric said.</p></blockquote>

    <p>Together, Coinbase and TestMachine are proving that scale and trust can move in lockstep, and that the next era of decentralized trading will be defined by intelligence, not compromise.</p>

    <p>Learn more about how TestMachine powers token safety at <a href="https://testmachine.ai/">testmachine.ai</a> or reach us at contact@testmachine.ai. Experience and learn more about <a href="https://www.coinbase.com/trade-crypto/dex">Coinbase DEX</a>.</p>

    <p class="article-disclaimer"><em>Disclaimer: Coinbase &amp; TestMachine make no guarantees that users will not be exposed to malicious tokens on Coinbase DEX.</em></p>

  {:else if post.slug === 'testmachine-vs-euler'}
    <p><em>Recreating a $196 million exploit.</em></p>

    <h3>Context</h3>

    <p>Euler Finance, a prominent decentralized finance (DeFi) lending protocol, suffered a significant exploit in March 2023. The hack resulted in the loss of approximately $196 million in cryptocurrency assets. The vulnerability stemmed from a subtle logic error in the protocol's smart contract that allowed a malicious actor to accumulate bad debt and subsequently liquidate their own position, effectively draining tokens from the protocol's vault.</p>

    <h3>How TestMachine Did It</h3>

    <p>TestMachine's AI-driven smart contract auditing system successfully replicated the Euler hack, demonstrating its ability to identify complex vulnerabilities that eluded human auditors. The system's approach highlighted several key advantages over traditional auditing methods:</p>

    <ol>
      <li><strong>Multi-agent simulation:</strong> TestMachine utilized two simulated Externally Owned Accounts (EOAs) in its campaign. This multi-agent approach allowed it to circumvent Euler's security measures, which were designed to prevent a single account from both taking out a loan and liquidating its own position. By using one EOA to borrow and another to liquidate, TestMachine exploited a blind spot in the protocol's security logic.</li>

      <li><strong>Identification of complex interaction patterns:</strong> The vulnerability in Euler's protocol arose from the intricate interplay between different contract functions. TestMachine's reinforcement learning algorithms excel at exploring vast state spaces and identifying non-obvious sequences of actions that can lead to exploitable states. This capability allowed it to discover the specific sequence of transactions that could breach the protocol's security.</li>

      <li><strong>Simplified exploit execution:</strong> While the original Euler hack employed a flash loan, requiring the attacker to bundle a complex sequence of transactions as raw calldata, TestMachine demonstrated a more straightforward approach. The system's simulated agents were initialized with sufficient funds, eliminating the need for borrowing. This simplification allowed TestMachine to execute the exploit through a series of individual transactions, which could later be adapted into a flash loan attack if needed.</li>

      <li><strong>Rapid iteration and learning:</strong> Unlike human auditors who might be constrained by time or cognitive limitations, TestMachine can rapidly iterate through thousands of potential attack vectors. This exhaustive exploration allows it to uncover vulnerabilities that might be overlooked in manual code reviews or more targeted testing approaches.</li>

      <li><strong>Invariant violation detection:</strong> TestMachine's ability to identify and exploit this vulnerability stemmed from its focus on violating key protocol invariants. In this case, the system recognized that the separation between borrower and liquidator accounts was a critical invariant that, if breached, could lead to unauthorised token extraction from the protocol's vault.</li>
    </ol>

    <p>By replicating the Euler hack, TestMachine demonstrated its capacity to identify sophisticated vulnerabilities in complex DeFi protocols. This case underscores the potential of AI-driven security auditing tools to complement and enhance traditional smart contract auditing processes, potentially preventing significant financial losses in the future.</p>

  {:else if post.slug === 'breaking-tokenomics'}
    <h3>Introduction</h3>

    <p>At TestMachine, we specialize in uncovering threats in smart contracts before they can be exploited. Automated security analysis is critical in the rapidly evolving world of blockchain and smart contracts, and the TestMachine platform uses a technique called reinforcement learning to autonomously scan and detect vulnerabilities in smart contracts at scale—without human intervention.</p>

    <p>Recently, TestMachine uncovered a severe vulnerability in the <a href="http://app.virtuals.io/">Virtuals AI Agent</a> protocol that could allow an attacker to artificially inflate token balances to near-maximum values, posing a significant risk to users and market integrity. This vulnerability was present despite the fact that Virtuals participated in at least <a href="https://whitepaper.virtuals.io/about-virtuals/important-links/security-audits">two human-conducted audits</a> from major auditing firms in the last year. We have reported the flaw to the Virtuals security team.</p>

    <p>TestMachine, on the other hand, can scan tens of thousands of tokens per hour, while human auditors cannot even identify such subtle vulnerabilities in a single contract over the course of a several week audit.</p>

    <p>Here we discuss the nature of the flaw, how TestMachine discovered it, and how TestMachine's automated, scalable, AI-driven security assessments will become increasingly necessary as digital assets proliferate and grow more complex.</p>

    <h3>What is Virtuals.io?</h3>

    <p><a href="http://virtuals.io/">Virtuals.io</a> is a decentralized AI agent platform that transforms autonomous, multimodal agents into tokenized, co-owned digital entities. Each agent operates across multiple platforms—games, social media, chat apps—with consistent memory and personality, powered by a modular AI framework called GAME. Using blockchain infrastructure, Virtuals enables fractional ownership of agents via fungible tokens, decentralized governance, and on-chain revenue sharing. Agents can earn and spend crypto, evolve through community contributions, and monetize through real-world applications, such as live-streaming or in-game interactions. By combining AI autonomy, immersive interaction, and tokenized economics, Virtuals.io pioneers a new class of intelligent, composable, and investable digital actors.</p>

    <p>Virtuals tokens are used within the ecosystem to facilitate operations, with taxation mechanisms built into their contracts to support liquidity, development, and operational costs. However, flaws in these taxation mechanisms can lead to unintended security vulnerabilities, as demonstrated by the issue TestMachine identified.</p>

    <h3>The Vulnerability: Unchecked Tax Processing</h3>

    <p>The flaw TestMachine discovered arises from an unchecked underflow in the contract's tax processing logic. If exploited, it could irreparably damage the integrity of Virtuals-based tokens, potentially leading to catastrophic devaluation and loss of trust in the ecosystem.</p>

    <p>The flaw resides in the <code>_taxProcessing</code> function of the Virtuals token implementation contract (address: <code>0x082Cb6e892Dd0699B5f0d22f7D2e638BBAdA5D94</code>).</p>

    <p>Specifically, the system's tax mechanism allows the <code>projectBuyTaxBasisPoints</code> or <code>projectSellTaxBasisPoints</code> to be set above <code>BP_DENOM</code>, leading to an arithmetic underflow when calculating post-tax token transfers.</p>

    <p>Since this calculation occurs within an <code>unchecked</code> block, it does not revert on error, potentially resulting in the recipient receiving nearly <code>uint256::MAX</code> tokens instead of the intended amount.</p>

    <h3>Why Is This Dangerous</h3>

    <p>TestMachine, via its reinforcement learning-driven penetration testing, identified that if <code>projectBuyTaxBasisPoints</code> is set higher than <code>BP_DENOM</code>, the tax deducted from a transaction could exceed the original amount, causing <code>amountLessTax_</code> to underflow. As a result, the recipient of the transaction would receive a large token balance, breaking the tax mechanism and invalidating the token economy entirely.</p>

    <p>In short:</p>

    <ul>
      <li>The contract allows the tax rate (<code>projectBuyTaxBasisPoints</code>) to be set to a value greater than 100% (<code>BP_DENOM = 10,000</code>).</li>
      <li>If this happens, the tax amount exceeds the transaction amount—leading to an underflow (i.e., subtraction resulting in a negative number, which wraps around to a massive positive value).</li>
      <li>Due to Solidity's <code>unchecked {'{}'}</code> block, this underflow doesn't trigger a revert. Instead, the receiving wallet is credited with nearly the maximum possible uint256 balance (≈2²⁵⁶ tokens).</li>
    </ul>

    <p>In simple terms, an attacker (or even the contract owner) could instantly inflate an account's token balance to near-infinity, all while keeping the total supply of the token unchanged—effectively destroying the token's economy.</p>

    <h3>Automated Discovery via TestMachine</h3>

    <p>Unlike traditional security audits that require manual review, TestMachine autonomously identified this flaw through its reinforcement learning-driven vulnerability detection. Within minutes of analyzing the Virtuals token contracts, TestMachine flagged the unchecked arithmetic operation as a potential high-risk underflow condition and generated a reproducible exploit scenario.</p>

    <p>TestMachine's ability to rapidly detect these kinds of subtle but critical issues demonstrates the power of AI-driven, automated security testing. As smart contract ecosystems grow more complex, automation is essential for uncovering vulnerabilities before they can be exploited in the wild.</p>

    <h3>Exploitation Risk: Who Can Trigger This?</h3>

    <p>For this exploit to be triggered, the tax rate needs to be manually set beyond the safe limit, and the user would need to have admin privileges. Although the taxes are only levied on pool addresses, an admin can add any address to that set. Normally, this might not seem like a concern—except:</p>

    <ul>
      <li>All Virtuals tokens share the same owner address (<code>0xE220…567</code>), which has unrestricted control over tax rates.</li>
      <li>This owner address lacks multi-signature security, meaning a single compromised key could result in a devastating attack.</li>
      <li>If an attacker gains control of this admin wallet, they could manipulate tax rates, force an underflow, and instantly hyperinflate selected accounts.</li>
    </ul>

    <h3>Recommended Fixes</h3>

    <h4>1. Remove Underflow Risk</h4>
    <p>Modify <code>_taxProcessing</code> to ensure that underflows trigger a revert.</p>

    <h4>2. Enforce Safe Tax Limits</h4>
    <p>Restrict <code>projectBuyTaxBasisPoints</code> and <code>projectSellTaxBasisPoints</code> to a maximum of <code>BP_DENOM</code> (10,000).</p>

    <h4>3. Improve Admin Security</h4>
    <ul>
      <li>Implement multi-signature protection for tax adjustments.</li>
      <li>Decentralize ownership or transition to on-chain governance for tax rate changes.</li>
      <li>Conduct regular TestMachine campaigns to detect the possibility of privilege escalations.</li>
      <li>Revoke admin privileges as necessary to limit the attack surface.</li>
    </ul>

    <h3>Conclusion: The Future of Smart Contract Security</h3>

    <p>This vulnerability underscores a broader issue in Web3: even well-intentioned, frequently audited smart contracts may still harbor catastrophic bugs if not rigorously tested using autonomous solutions.</p>

    <p>TestMachine is leading the charge in proactive security, uncovering exploits before they can be weaponized. As the DeFi and AI-agent space continues to expand, projects must embrace automated, self-evolving security tools to stay ahead of attackers. It's security through offense rather than passive inspection.</p>

    <p>Want to ensure your contracts are secure? <a href="https://app.testmachine.ai/">Run a TestMachine scan today</a>.</p>

  {:else if post.slug === 'funding-announcement'}
    <p><em>TestMachine has raised $6.5M to scale its AI-driven platform monitoring 1M+ tokens and delivering real-time blockchain security for exchanges, developers, and DeFi.</em></p>

    <p><a href="http://www.testmachine.ai/">TestMachine</a>, the AI-driven blockchain security company safeguarding billions in digital assets, today announced it has raised $6.5 million in venture funding to accelerate the global rollout of its flagship platform. The round was led by BlockChange Ventures, Decasonic, Delphi Ventures, and New Form Capital. The investment will accelerate the development and deployment of TestMachine's flagship AI platform, which is now fully integrated into Coinbase's token security and deployment process.</p>

    <p>Other participants in the round included Baboon, UDHC, Auros Global, Generative Ventures, Contango Digital and Santiago Santos. The funding will support the further development of TestMachine's high-fidelity simulation capabilities.</p>

    <p>TestMachine's groundbreaking automated security approach has been implemented and trusted by leaders across the digital asset industry. Coinbase has integrated TestMachine into its CEX and newly launched DEX trading feature, using TestMachine to scan millions of tokens across multiple risk factors on a daily basis. With TestMachine, it takes just seconds to analyze an asset for risks, faster and deeper than any manual process.</p>

    <p>A Coinbase blockchain security engineer commented:</p>

    <blockquote><p>"Our team evaluated publicly available tools to identify and flag ERC-20 custodiability risks, …We rigorously assessed these tools over a three-stage work trial, and TestMachine stood out because it achieved 100% accuracy on all tokens (with no false positives or negatives), even catching human errors in our own reviews."</p></blockquote>

    <p>Dr. Matthew J. Lewis, CEO of TestMachine, emphasized the growing importance of automated security in the blockchain sector.</p>

    <blockquote><p>"As blockchain networks and decentralized finance continue to expand, the complexity and volume of transactions make it increasingly difficult to detect vulnerabilities manually." He said "Our goal is to empower investors, developers, and exchanges with tools that proactively identify, monitor, and mitigate risks before they manifest."</p></blockquote>

    <p>The funding will help TestMachine expand across crypto exchanges, custodians, market makers and DeFi protocols. By simulating real-world attacks before they happen, the platform empowers the industry to stay ahead of threats—delivering stronger, more reliable blockchain infrastructure for the entire ecosystem.</p>

    <h3>About TestMachine</h3>

    <p><a href="http://www.testmachine.ai/">TestMachine</a> was founded in 2021 with a core mission: to ensure the safety, transparency, and reliability of smart contracts, digital assets, and blockchain infrastructure—empowering a secure and trustworthy decentralized future. TestMachine's flagship platform provides developers, DeFi protocols, traders, and exchanges with an AI-driven, continuous security platform that detects, mitigates, and prevents risk in real time.</p>

    <p>TestMachine leverages AI to dynamically identify and exploit smart contract vulnerabilities. Unlike traditional code audits, it offers a complete view of blockchain assets—from economic risk to software vulnerabilities and semantic behaviors. By combining continuous testing with real-time behavioral analysis, TestMachine delivers the most proactive and adaptive layer of security in Web3.</p>

    <p>TestMachine is currently monitoring over 1 million tokens in real time, surfacing an unprecedented volume of real-world risks. From a validation set of 11,000 tokens, the system correctly identified 100% of rug pulls, totaling over $120M in value.</p>

    <p>Users can visit <a href="http://www.testmachine.ai/">www.testmachine.ai</a> for more information and to get started today!</p>

  {:else if post.slug === 'auditagent-vs-azimuth'}
    <p>AI tools are rapidly becoming part of the smart contract security workflow. Some teams experiment with them internally, while others are beginning to rely on them as a <strong>first line of defense before manual audits</strong>.</p>

    <p>But a fundamental question remains: how well do these tools actually perform when analyzing real codebases?</p>

    <p>To explore that question, we ran a comparison between two systems:</p>

    <ul>
      <li><strong>AuditAgent</strong> — a static AI auditing tool focused on vulnerability detection</li>
      <li><strong>Azimuth</strong> — a behavioral analysis engine designed to simulate exploit paths and protocol interactions</li>
    </ul>

    <p>Rather than evaluating a single repository, we tested both systems across <strong>four different codebases</strong>, each representing a different category of smart contract architecture.</p>

    <h3>Repositories Analyzed</h3>

    <ul>
      <li>PrimeVault — DeFi protocol (<a href="https://app.testmachine.ai/share/repo/c6fcb6e8cc8957d91553da408da5f9095ac40f4080bd7d59cfb6cb6c93cc390d" target="_blank" rel="noopener noreferrer">view report</a>)</li>
      <li>LendMachine — Lending protocol (<a href="https://app.testmachine.ai/share/repo/38a3f72dcda7de29dad5e7d4eb70a1795419eab1891b905f6274a4a413b235d6" target="_blank" rel="noopener noreferrer">view report</a>)</li>
      <li>Murky — Utility library (<a href="https://app.testmachine.ai/share/repo/74d4d96ff14ef2a019f827634d9acdc4a61956cb1f1631b89689aa377772bc47" target="_blank" rel="noopener noreferrer">view report</a>)</li>
      <li>BaseTap — Payment protocol (<a href="https://app.testmachine.ai/share/repo/c5ce0498654d9d14d29ddfcacdee97c30ca8e48ee0938a9722df364bc5e8d3f1" target="_blank" rel="noopener noreferrer">view report</a>)</li>
    </ul>

    <p>Each repository was analyzed independently by both systems and compared across several dimensions:</p>

    <ul>
      <li>vulnerability detection</li>
      <li>exploit modeling</li>
      <li>protocol reasoning</li>
      <li>workflow analysis</li>
      <li>code-quality observations</li>
    </ul>

    <p>The results were instructive.</p>

    <h3>Methodology</h3>

    <p>For each repository we:</p>

    <ul>
      <li>Ran <strong>AuditAgent</strong> to generate a full audit report</li>
      <li>Ran <strong>Azimuth</strong> to generate behavioral exploit hypotheses</li>
      <li>Compared the outputs across several dimensions:
        <ul>
          <li>number of findings</li>
          <li>exploit depth</li>
          <li>cross-contract reasoning</li>
          <li>economic attack modeling</li>
          <li>operational failure modes</li>
        </ul>
      </li>
    </ul>

    <p>Importantly, these were <strong>unmodified repositories</strong> analyzed in their original state.</p>

    <h3>Repository 1 — PrimeVault</h3>

    <p>Full analysis: <a href="https://app.testmachine.ai/share/repo/c6fcb6e8cc8957d91553da408da5f9095ac40f4080bd7d59cfb6cb6c93cc390d" target="_blank" rel="noopener noreferrer">view Azimuth report</a></p>

    <p>PrimeVault is a DeFi protocol with vault mechanics and capital flows between multiple contracts.</p>

    <p>This kind of architecture introduces several attack surfaces:</p>

    <ul>
      <li>asset accounting</li>
      <li>permission controls</li>
      <li>economic manipulation</li>
      <li>cross-contract interactions</li>
    </ul>

    <div class="blog-compare-grid">
      <div class="blog-compare-header">Capability</div>
      <div class="blog-compare-header">AuditAgent</div>
      <div class="blog-compare-header blog-compare-az">Azimuth</div>

      <div class="blog-compare-label">Static vulnerability detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Cross-function exploit discovery</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Protocol economic modeling</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Multi-contract reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Implementation &amp; best-practice feedback</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐</div>
    </div>

    <p>AuditAgent correctly identified several <strong>isolated contract risks</strong> and best-practice violations.</p>

    <p>Azimuth expanded those issues into <strong>realistic exploit paths</strong>, including scenarios where attackers could manipulate protocol flows across multiple contracts.</p>

    <p>This is a recurring theme: static scanners can identify risky code patterns, but often stop short of modeling <strong>how those risks translate into real attacks</strong>.</p>

    <h3>Repository 2 — LendMachine</h3>

    <p>Full analysis: <a href="https://app.testmachine.ai/share/repo/38a3f72dcda7de29dad5e7d4eb70a1795419eab1891b905f6274a4a413b235d6" target="_blank" rel="noopener noreferrer">view Azimuth report</a></p>

    <p>LendMachine is a simplified lending protocol with collateral, borrowing, liquidation, and reward mechanics.</p>

    <p>These systems are especially sensitive to <strong>economic exploits</strong>, where small logic flaws can create large financial consequences.</p>

    <div class="blog-compare-grid">
      <div class="blog-compare-header">Capability</div>
      <div class="blog-compare-header">AuditAgent</div>
      <div class="blog-compare-header blog-compare-az">Azimuth</div>

      <div class="blog-compare-label">Static vulnerability detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Reentrancy detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Cross-function exploit discovery</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Economic attack modeling</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Protocol reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>
    </div>

    <p>Both tools detected a configuration risk around <strong>interest rate control</strong>.</p>

    <p>AuditAgent noted that the interest rate setter lacked access control. Azimuth went further and modeled several exploit scenarios:</p>

    <ul>
      <li>artificially inflating interest rates to force liquidations</li>
      <li>temporarily setting rates to zero to disable accrual</li>
      <li>manipulating borrower health factors during liquidation windows</li>
    </ul>

    <p>Additionally, Azimuth identified issues in <strong>reward accounting synchronization</strong>, which could lead to phantom reward accumulation under certain conditions.</p>

    <p>These types of vulnerabilities are difficult for static scanners to detect because they require reasoning about <strong>state transitions across multiple transactions</strong>.</p>

    <h3>Repository 3 — Murky</h3>

    <p>Full analysis: <a href="https://app.testmachine.ai/share/repo/74d4d96ff14ef2a019f827634d9acdc4a61956cb1f1631b89689aa377772bc47" target="_blank" rel="noopener noreferrer">view Azimuth report</a></p>

    <p>Murky is not a protocol at all.</p>

    <p>It is a <strong>Merkle tree utility library</strong> used primarily for testing and proof generation.</p>

    <p>That makes it an interesting control case.</p>

    <p>Because Murky has:</p>

    <ul>
      <li>no capital flows</li>
      <li>no incentives</li>
      <li>no multi-contract architecture</li>
    </ul>

    <p>...the number of meaningful attack surfaces is naturally limited.</p>

    <div class="blog-compare-grid">
      <div class="blog-compare-header">Capability</div>
      <div class="blog-compare-header">AuditAgent</div>
      <div class="blog-compare-header blog-compare-az">Azimuth</div>

      <div class="blog-compare-label">Static vulnerability detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐</div>

      <div class="blog-compare-label">Merkle logic analysis</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Edge-case reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Protocol exploit modeling</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐</div>
    </div>

    <p>In this case, both tools performed similarly.</p>

    <p>AuditAgent produced a larger set of <strong>code hygiene observations</strong>, including style issues and gas optimizations.</p>

    <p>Azimuth focused more on <strong>edge cases in Merkle proof verification</strong>, such as malformed trees and integration misuse.</p>

    <p>But the differences were much smaller than in protocol repositories. This is expected. When the codebase is a simple utility library, there are simply <strong>fewer opportunities for exploit modeling to add value</strong>.</p>

    <h3>Repository 4 — BaseTap</h3>

    <p>Full analysis: <a href="https://app.testmachine.ai/share/repo/c5ce0498654d9d14d29ddfcacdee97c30ca8e48ee0938a9722df364bc5e8d3f1" target="_blank" rel="noopener noreferrer">view Azimuth report</a></p>

    <p>BaseTap is a modular payment protocol designed around taps, which allow controlled token flows between accounts.</p>

    <p>The system includes:</p>

    <ul>
      <li>tap registries</li>
      <li>execution contracts</li>
      <li>payment sessions</li>
      <li>batching logic</li>
      <li>split payments</li>
    </ul>

    <p>This architecture introduces several workflow risks.</p>

    <div class="blog-compare-grid">
      <div class="blog-compare-header">Capability</div>
      <div class="blog-compare-header">AuditAgent</div>
      <div class="blog-compare-header blog-compare-az">Azimuth</div>

      <div class="blog-compare-label">Static vulnerability detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Access-control analysis</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Workflow reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Payment-flow exploit modeling</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Cross-contract reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>
    </div>

    <p>AuditAgent identified several important issues, including:</p>

    <ul>
      <li>missing authorization checks</li>
      <li>inconsistencies between <code>canExecute()</code> and <code>executeTap()</code></li>
      <li>architectural design weaknesses</li>
    </ul>

    <p>Azimuth expanded these into <strong>attack scenarios affecting real users</strong>.</p>

    <ul>
      <li>a payment session could be griefed by malicious actors calling <code>markPaid()</code> before legitimate settlement</li>
      <li>ETH transfers could become permanently locked when interacting with ERC20 tap paths</li>
      <li>tap owners could inflate payment amounts after users grant approvals</li>
    </ul>

    <p>These are not simply coding errors. They are <strong>product trust failures</strong>, where legitimate users could be harmed even though the contract technically behaves as written.</p>

    <h3>Cross-Repository Comparison</h3>

    <p>Looking across the four repositories reveals a consistent pattern.</p>

    <div class="blog-compare-grid">
      <div class="blog-compare-header">Capability</div>
      <div class="blog-compare-header">AuditAgent</div>
      <div class="blog-compare-header blog-compare-az">Azimuth</div>

      <div class="blog-compare-label">Static vulnerability detection</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Cross-function exploit discovery</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Protocol economic modeling</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Workflow / state-machine reasoning</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-highlight blog-compare-stars">⭐⭐⭐⭐⭐</div>

      <div class="blog-compare-label">Implementation &amp; best-practice feedback</div>
      <div class="blog-compare-cell blog-compare-aa blog-compare-stars">⭐⭐⭐</div>
      <div class="blog-compare-cell blog-compare-az blog-compare-stars">⭐⭐⭐</div>
    </div>

    <p>Each tool excels in different areas.</p>

    <p><strong>AuditAgent strengths</strong></p>

    <ul>
      <li>strong static analysis</li>
      <li>best-practice detection</li>
      <li>architectural hygiene</li>
    </ul>

    <p><strong>Azimuth strengths</strong></p>

    <ul>
      <li>exploit path modeling</li>
      <li>economic attack analysis</li>
      <li>multi-contract reasoning</li>
      <li>workflow failure detection</li>
    </ul>

    <h3>What This Means for AI Auditing</h3>

    <p>The results suggest an important distinction between two categories of AI security tools.</p>

    <h4>Static AI auditors</h4>

    <p>These tools behave similarly to traditional vulnerability scanners.</p>

    <p>They excel at identifying:</p>

    <ul>
      <li>reentrancy risks</li>
      <li>missing access control</li>
      <li>unsafe patterns</li>
      <li>implementation issues</li>
    </ul>

    <p>But they often struggle to reason about:</p>

    <ul>
      <li>multi-step attacks</li>
      <li>economic incentives</li>
      <li>protocol workflows</li>
    </ul>

    <h4>Behavioral security engines</h4>

    <p>Systems like Azimuth focus less on pattern matching and more on <strong>simulating how contracts behave under adversarial conditions</strong>.</p>

    <p>This enables them to surface vulnerabilities that appear only when:</p>

    <ul>
      <li>multiple transactions interact</li>
      <li>cross-contract calls occur</li>
      <li>incentives are manipulated</li>
    </ul>

    <h3>The Bigger Picture</h3>

    <p>Smart contract security is evolving.</p>

    <p>Early tools focused on <strong>code correctness</strong>.</p>

    <p>Modern protocols require analysis of <strong>economic behavior and system interactions</strong>.</p>

    <p>Both layers matter.</p>

    <p>Static scanners are valuable for quickly catching implementation mistakes.</p>

    <p>But as protocols grow more complex, security tools must also understand:</p>

    <ul>
      <li>how users interact with systems</li>
      <li>how attackers manipulate incentives</li>
      <li>how state transitions create unexpected behavior</li>
    </ul>

    <h3>Conclusion</h3>

    <p>Across the four repositories we analyzed — PrimeVault, LendMachine, Murky, and BaseTap — a consistent pattern emerged.</p>

    <p>Static AI auditors were effective at identifying <strong>common vulnerability patterns and implementation risks</strong>.</p>

    <p>But the most meaningful issues surfaced when the analysis moved beyond individual functions and began modeling <strong>how contracts behave as a system</strong>.</p>

    <p>Many of the highest-impact findings depended on:</p>

    <ul>
      <li>multi-step interactions</li>
      <li>cross-contract workflows</li>
      <li>economic incentives</li>
      <li>real user behavior</li>
    </ul>

    <p>Static analysis is an important first layer of defense, but modern smart contract exploits rarely arise from a single unsafe line of code.</p>

    <p>They emerge from how components interact over time.</p>

    <p><strong>Static analysis tells you where the code looks risky.</strong></p>

    <p><strong>Dynamic analysis tells you how the system actually breaks.</strong></p>

    <p>As smart contracts continue to grow in complexity, behavioral analysis will increasingly become a necessary complement to static scanning in serious security workflows.</p>

  {:else if post.slug === 'testmachine-vs-llms'}
    <p><em>The research is in: LLMs cannot secure smart contracts. Here's why execution-driven reinforcement learning is the only reliable approach.</em></p>

    <h3>Overview</h3>

    <p>When we first published this post in late 2024, our argument that large language models are fundamentally insufficient for smart contract security was based on first principles and early observations. Since then, a wave of peer-reviewed research from teams at Georgia Tech, the University of Sydney, and elsewhere has confirmed this thesis with hard data. The numbers are worse than even we expected.</p>

    <p>Crypto losses from hacks and scams reached $1.7 billion in just the first four months of 2025—already surpassing the $1.49 billion total for all of 2024. Immunefi CEO Mitchell Amador called it "the worst quarter for hacks in history." According to <a href="https://www.halborn.com/" target="_blank" rel="noopener noreferrer">Halborn's Top 100 DeFi Hacks Report</a>, attackers are expanding their focus to gaming protocols and Layer 2 chains, with off-chain vulnerabilities accounting for a growing share of losses. The attack surface is expanding, and the tools the industry relies on are not keeping up.</p>

    <p>This updated post incorporates the latest research to make a clear, evidence-backed case: LLMs are useful assistants, but they are not security tools. TestMachine's reinforcement learning approach—which discovers vulnerabilities by actually executing exploits in a simulated environment—remains the only method that delivers zero false positives and catches the vulnerabilities that static analysis misses.</p>

    <h3>The Rise of LLMs in Security Tooling</h3>

    <p>Large language models have become remarkably capable. Models like GPT-4, Claude, and their successors can generate code, explain complex systems, and even reason about software architecture with surprising fluency. Naturally, the security community has explored whether these models can be applied to smart contract auditing—a domain where human expertise is scarce, expensive, and slow.</p>

    <p>The appeal is obvious. LLMs have been trained on vast corpora of code and text, including Solidity documentation, audit reports, and known vulnerability patterns. They can process an entire contract in seconds and produce plausible-sounding vulnerability assessments. Several startups and open-source projects now offer LLM-powered security scanning, and many developers use ChatGPT or similar tools as a first pass before engaging human auditors.</p>

    <p>We do not dismiss these advances. LLMs are genuinely useful for code comprehension, documentation, and even generating initial test scaffolding. TestMachine itself uses LLMs downstream—once our RL engine discovers a real exploit, we leverage language models to explain the attack path to developers and generate unit tests. This is an appropriate use of the technology: synthesizing and communicating information, not making security judgments.</p>

    <p>The problem arises when LLMs are treated as security oracles—trusted to determine whether a contract is safe or vulnerable. The research now conclusively shows that they cannot do this reliably.</p>

    <h3>What the Research Actually Shows</h3>

    <p>A series of studies published in 2025 have systematically evaluated LLM performance on smart contract vulnerability detection. The results paint a consistent picture of a technology that is fundamentally mismatched with the demands of security analysis.</p>

    <p><strong>Precision is catastrophically low.</strong> <a href="https://arxiv.org/abs/2309.05520" target="_blank" rel="noopener noreferrer">Wang et al. (2025)</a>, published in ACM Transactions on Software Engineering and Methodology, found that GPT-4 achieves only 22.6% precision when pinpointing smart contract vulnerabilities. GPT-3.5 scored 19.7%, and GPT-4o scored 20.2%. While recall can reach 88.2% for GPT-4, the low precision means roughly 4 out of every 5 issues flagged by the model are false positives. A tool that cries wolf 80% of the time is not a security tool—it is a noise generator that wastes developer time and erodes trust in automated analysis.</p>

    <p><strong>Modern contracts break LLMs even further.</strong> <a href="https://arxiv.org/abs/2501.07058" target="_blank" rel="noopener noreferrer">Wang et al. (January 2025)</a> examined how LLMs perform on contracts written in Solidity v0.8 and later, which include built-in overflow protections and other safety features. They found that even well-designed prompts only reduce false-positive rates by approximately 60%, and recall for detecting vulnerabilities in these modern contracts dropped to just 13% compared to older versions. The study revealed that LLMs rely heavily on recognizing patterns from established libraries rather than understanding the actual logic of a contract. When the code deviates from familiar patterns—as novel contracts inevitably do—the models fail silently.</p>

    <p><strong>No single LLM is consistently reliable.</strong> <a href="https://arxiv.org/abs/2512.02069" target="_blank" rel="noopener noreferrer">Yuan et al. (November 2025)</a> at Georgia Tech developed LLMBugScanner, which combines multiple LLMs in an ensemble approach to improve detection. Their key finding was that individual pre-trained LLMs exhibit inconsistent predictions across vulnerability types—no single model consistently outperforms others. Even their ensemble approach, which combines the outputs of multiple models, only achieves 60% top-5 detection accuracy on real CVE-labeled contracts. If the best-case scenario requires running multiple LLMs and still only catches 60% of known vulnerabilities, the technology is not ready for production security.</p>

    <p><strong>LLMs require supervision, not autonomy.</strong> <a href="https://www.sciencedirect.com/journal/expert-systems-with-applications" target="_blank" rel="noopener noreferrer">Ferraro et al. (July 2025)</a>, published in Expert Systems with Applications, conducted a broad evaluation of LLMs in smart contract development and concluded plainly: "LLMs require security auditing and are not yet suitable for unsupervised deployment." This is not a fringe opinion—it is the consensus of the research community.</p>

    <h3>Why LLMs Fail at Security</h3>

    <p>The research findings are not surprising when you understand how LLMs work. These models are trained to predict the next token in a sequence based on statistical patterns in their training data. They do not execute code. They do not track state changes. They do not reason about the consequences of a sequence of transactions interacting with a live contract.</p>

    <p>Smart contract vulnerabilities are fundamentally about <em>behavior</em>, not <em>syntax</em>. A contract can look perfectly reasonable in its source code while harboring a devastating exploit that only manifests through a specific sequence of state transitions. Reentrancy attacks, flash loan exploits, governance manipulation, and oracle manipulation all depend on dynamic interactions between contracts and external state—exactly the kind of reasoning that LLMs cannot perform.</p>

    <p>LLMs pattern-match against known vulnerability templates. When they encounter code that resembles a previously documented exploit, they flag it—often correctly for the well-known cases, but also frequently for benign code that happens to share syntactic similarity. And when they encounter a novel vulnerability that doesn't match any known template, they miss it entirely. This is why recall drops to 13% on modern Solidity contracts: the patterns have changed, and the models haven't adapted.</p>

    <h3>Independent Validation: The A1 System</h3>

    <p>Perhaps the most compelling validation of TestMachine's approach comes from independent academic research. <a href="https://arxiv.org/abs/2507.05558" target="_blank" rel="noopener noreferrer">Zhou (July 2025)</a> at the University of Sydney developed A1, an agentic system for smart contract exploit generation. The paper's conclusion is direct: "naïvely prompted LLMs generate unverified vulnerability speculations, leading to high false positive rates."</p>

    <p>The A1 system achieved a 62.96% success rate specifically by adopting an execution-driven validation approach—it eliminates false positives by only reporting vulnerabilities that produce actual profitable exploits. This is precisely the methodology TestMachine has used since its inception: don't speculate about what might be vulnerable, prove it by executing the exploit.</p>

    <p>The A1 paper independently validates the core insight behind TestMachine: execution feedback is essential, not just code analysis. The difference is that TestMachine has been operationalizing this principle at production scale for years, continuously monitoring over a million tokens with reinforcement learning agents that learn and adapt from every interaction.</p>

    <h3>TestMachine is Different</h3>

    <p>TestMachine uses reinforcement learning to actively discover vulnerabilities by executing exploit attempts against smart contracts in a high-fidelity simulated environment. This is not code review. This is adversarial testing—the same approach a real attacker would use, but conducted safely and systematically before deployment.</p>

    <p>The RL agent interacts with contract logic by constructing sequences of transactions, observing state changes, and receiving reward signals when exploits succeed. Over many iterations, the agent learns to discover attack paths that drain tokens, violate access controls, manipulate governance, or exploit economic logic. These are not hypothetical vulnerabilities flagged by pattern matching—they are proven exploits with concrete transaction sequences.</p>

    <p>This execution-driven approach has several fundamental advantages over LLM-based analysis:</p>

    <ul>
      <li><strong>Zero false positives by construction.</strong> TestMachine either executes a successful exploit or it doesn't. There is no ambiguity, no speculation, and no noise. Every finding is a proven vulnerability with a reproducible attack path. Compare this to GPT-4's 22.6% precision, where 4 out of 5 flagged issues are false alarms.</li>
      <li><strong>State-aware reasoning.</strong> The RL agent tracks the full state of the contract across a sequence of transactions. It can discover vulnerabilities that only emerge through multi-step interactions—flash loan attacks, reentrancy chains, cross-contract exploits—that are invisible to static code analysis.</li>
      <li><strong>Adaptation to novel code.</strong> Unlike LLMs, whose performance degrades sharply on modern Solidity contracts that deviate from training data patterns, TestMachine's agents learn from the contract's actual behavior. They don't need to have seen a similar vulnerability before—they discover new ones through exploration.</li>
      <li><strong>Continuous monitoring.</strong> Smart contracts evolve. Proxies get re-pointed, permissions change, and new interactions emerge. TestMachine continuously re-evaluates contracts as they change, catching behavioral shifts that a one-time LLM audit would miss entirely.</li>
      <li><strong>Quantified security metrics.</strong> TestMachine produces measurable attack success rates that allow developers to track risk reduction across contract versions. This turns security from a binary "audited/not audited" judgment into a continuous, quantifiable process.</li>
      <li><strong>LLMs where they belong.</strong> TestMachine uses LLMs downstream for what they're good at: once the RL engine discovers a real exploit, language models generate clear explanations of the attack path, suggest fixes, and produce unit tests that encode the vulnerability. This is LLMs as communication tools, not security oracles.</li>
    </ul>

    <p>The research is now unambiguous. LLMs achieve 22.6% precision at best, drop to 13% recall on modern contracts, and even multi-model ensembles only reach 60% detection on known vulnerabilities. Meanwhile, execution-driven approaches like TestMachine and the independently developed A1 system eliminate false positives entirely by grounding every finding in actual exploit execution. As the scale of crypto losses continues to grow—$1.7 billion in early 2025 alone—the industry cannot afford to rely on tools that generate more noise than signal. Smart contract security demands proof, not prediction.</p>

  {:else if post.slug === 'sherlock-bountiability-benchmark'}
    <SherlockBenchmark />
  {:else}
    {@html post.content}
  {/if}
</article>
